import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { createI18n } from 'vue-i18n';
import PrimeVue from 'primevue/config';
import Aura from '@primevue/themes/aura';
import { definePreset } from '@primevue/themes';
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';
import PlutosVueLib from '@plutos/vuelib';
import * as Sentry from '@sentry/vue';

// Version info injected at build time
declare const __APP_VERSION__: string;
declare const __GIT_COMMIT__: string;
declare const __BUILD_TIME__: string;
declare const __VUELIB_VERSION__: string;

function formatRelativeBuildTime(buildTime: string): string {
  if (!buildTime || buildTime === 'dev') {
    return 'dev';
  }

  const parsed = new Date(buildTime);
  if (Number.isNaN(parsed.getTime())) {
    return 'unknown';
  }

  const diffMs = parsed.getTime() - Date.now();
  const absMs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

  if (absMs < 60_000) {
    return rtf.format(Math.round(diffMs / 1000), 'second');
  }
  if (absMs < 3_600_000) {
    return rtf.format(Math.round(diffMs / 60_000), 'minute');
  }
  if (absMs < 86_400_000) {
    return rtf.format(Math.round(diffMs / 3_600_000), 'hour');
  }
  return rtf.format(Math.round(diffMs / 86_400_000), 'day');
}

const buildTime = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : 'dev';

// Log version info for debugging in production
console.log(
  '%c🚀 Plutos Payment Frontend',
  'color: #6366f1; font-weight: bold; font-size: 14px;'
);
console.table({
  'App Version': typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev',
  'Git Commit': typeof __GIT_COMMIT__ !== 'undefined' ? __GIT_COMMIT__ : 'dev',
  'Build Time': buildTime,
  'Built': formatRelativeBuildTime(buildTime),
  'Vuelib Version': typeof __VUELIB_VERSION__ !== 'undefined' ? __VUELIB_VERSION__ : 'dev',
  'Environment': import.meta.env.MODE,
});

// Custom dark theme based on Aura with Indigo primary
const PaymentTheme = definePreset(Aura, {
  semantic: {
    primary: {
      50: '{indigo.50}',
      100: '{indigo.100}',
      200: '{indigo.200}',
      300: '{indigo.300}',
      400: '{indigo.400}',
      500: '{indigo.500}',
      600: '{indigo.600}',
      700: '{indigo.700}',
      800: '{indigo.800}',
      900: '{indigo.900}',
      950: '{indigo.950}'
    },
    colorScheme: {
      dark: {
        surface: {
          0: '#ffffff',
          50: '{zinc.50}',
          100: '{zinc.100}',
          200: '{zinc.200}',
          300: '{zinc.300}',
          400: '{zinc.400}',
          500: '{zinc.500}',
          600: '{zinc.600}',
          700: '{zinc.700}',
          800: '{zinc.800}',
          900: '{zinc.900}',
          950: '{zinc.950}'
        }
      }
    }
  }
});

import App from './App.vue';
import router from './router';
import en from './locales/en.json';
import de from './locales/de.json';
import pl from './locales/pl.json';

// Styles - Order matters: base first, then app styles, then vuelib components (highest priority)
import 'primeicons/primeicons.css';
import './assets/main.css';
import '@plutos/vuelib/dist/vuelib.css';

// Detect user's preferred language
const browserLang = navigator.language.split('-')[0];
const defaultLocale = ['en', 'de', 'pl'].includes(browserLang) ? browserLang : 'en';

const getCookie = (name: string) => {
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (const cookie of cookies) {
    const [key, ...rest] = cookie.split('=');
    if (key === name) return decodeURIComponent(rest.join('='));
  }
  return null;
};

const setCookie = (name: string, value: string) => {
  const parts: string[] = [`${name}=${encodeURIComponent(value)}`, 'Path=/', 'SameSite=Lax', `Max-Age=${60 * 60 * 24 * 365}`];
  const host = window.location.hostname;
  if (host === 'plutos.trading' || host.endsWith('.plutos.trading')) parts.push('Domain=.plutos.trading');
  if (window.location.protocol === 'https:') parts.push('Secure');
  document.cookie = parts.join('; ');
};

const cookieLocale = getCookie('locale');
const storedLocale = localStorage.getItem('locale');
const initialLocale = (cookieLocale ?? storedLocale ?? defaultLocale);

if (!storedLocale && initialLocale) localStorage.setItem('locale', initialLocale);
if (!cookieLocale && initialLocale) setCookie('locale', initialLocale);

const i18n = createI18n({
  legacy: false,
  locale: initialLocale,
  fallbackLocale: 'en',
  messages: { en, de, pl },
});

const app = createApp(App);

// Initialize Sentry for error tracking
if (import.meta.env.VITE_PAYMENT_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_PAYMENT_SENTRY_DSN,
    environment: import.meta.env.MODE || 'production',
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false, // Payment service should not send PII
    release: import.meta.env.VITE_PAYMENT_VERSION || 'payment-frontend@unknown',
  });
}

app.use(createPinia());
app.use(router);
app.use(i18n);

// Setup PrimeVue with dark theme
app.use(PrimeVue, {
  ripple: true,
  theme: {
    preset: PaymentTheme,
    options: {
      prefix: 'p',
      darkModeSelector: '.dark',
      cssLayer: false
    }
  }
});
app.use(ToastService);
app.use(ConfirmationService);

// Setup PlutosVueLib
(PlutosVueLib as any).install(app, {
  HUB_APP_URL: import.meta.env.VITE_PLUTOS_HUB_URL || 'https://hub.plutos.trading',
  NFT_APP_URL: import.meta.env.VITE_PLUTOS_NFT_URL || 'https://plutos.trading',
  CORE_APP_URL: import.meta.env.VITE_PLUTOS_CORE_URL || 'https://core.plutos.trading',
});

// Hide splash screen when app is ready
app.mount('#app');

const splash = document.getElementById('splash-screen');
if (splash) {
  splash.classList.add('hidden');
  setTimeout(() => splash.remove(), 300);
}
